import { Component } from '@theme/component';

/**
 * ファーストビューのスライドショー。スライドをフェードで切り替える。
 *
 * 表示中のスライドは aria-hidden="false" を持ち、見た目の切り替えは CSS の
 * transition に任せる。JavaScript は現在位置の管理と自動再生だけを受け持つ。
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides - スライド要素
 * @property {HTMLElement[]} [dots] - ページネーションのボタン
 *
 * @extends Component<Refs>
 */
class HeroSlideshowComponent extends Component {
  requiredRefs = ['slides'];

  /** @type {number} 表示中のスライドの0始まりの連番 */
  #current = 0;

  /** @type {number | null} */
  #timer = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#slides.length < 2 || this.#autoplayDelay === 0) return;

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#resume();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#pause();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * 指定したスライドへ切り替える。ページネーションから呼ばれる。
   * @param {string | number} index - 切り替え先の0始まりの連番
   */
  select(index) {
    const slides = this.#slides;
    const target = Number(index);

    if (slides.length === 0 || Number.isNaN(target)) return;

    // 手動で操作されたら自動再生は止める
    this.#stop();

    this.#current = ((target % slides.length) + slides.length) % slides.length;
    this.#render();
  }

  get #slides() {
    return this.refs.slides ?? [];
  }

  /** 自動再生の間隔（ミリ秒）。0 なら自動再生しない。 */
  get #autoplayDelay() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

    const seconds = Number(this.getAttribute('autoplay'));

    return Number.isNaN(seconds) ? 0 : seconds * 1000;
  }

  #render() {
    this.#slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === this.#current ? 'false' : 'true');
    });

    this.refs.dots?.forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === this.#current ? 'true' : 'false');
    });
  }

  #next = () => {
    this.#current = (this.#current + 1) % this.#slides.length;
    this.#render();
  };

  #resume = () => {
    const delay = this.#autoplayDelay;

    if (this.#timer !== null || delay === 0) return;

    this.#timer = window.setInterval(this.#next, delay);
  };

  #pause = () => {
    if (this.#timer === null) return;

    window.clearInterval(this.#timer);
    this.#timer = null;
  };

  /** 以後いっさい自動再生しない */
  #stop = () => {
    this.#pause();
    this.removeAttribute('autoplay');
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pause();
    } else {
      this.#resume();
    }
  };
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshowComponent);
}
